import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Bike,
  Bus,
  Car,
  Footprints,
  Info,
  Lock,
  House,
  Navigation,
  Trash2,
  Unlock,
} from 'lucide-react';
import type {
  DayBase,
  ItineraryItemSummary,
  TransportLegSummary,
  TransportMode,
} from '@tappa/shared';
import { DataBadge, cn } from '@tappa/ui';
import { formatDistance, formatDuration, formatVisitDuration } from '@tappa/shared';
import { openNavigation } from '../../lib/navigation.js';
import { useGeolocation } from '../../lib/geolocation.js';
import { PlaceThumb } from '../places/PlaceImage.js';

/**
 * Timeline della giornata.
 *
 * Gli spostamenti fanno parte della timeline, non sono note a pie' di pagina:
 * e' il tempo fra una tappa e l'altra che rende una giornata fattibile o no.
 *
 * Riordino: pulsanti su/giu' invece del solo drag & drop. Funzionano al primo
 * tocco su mobile, da tastiera e con screen reader — il drag li affiancherà,
 * non li sostituirà.
 */

const MODE_ICONS: Record<TransportMode, typeof Footprints> = {
  walk: Footprints,
  bike: Bike,
  car: Car,
  transit: Bus,
  taxi: Car,
};

const MODE_LABELS: Record<TransportMode, string> = {
  walk: 'a piedi',
  bike: 'in bici',
  car: 'in auto',
  transit: 'con i mezzi',
  taxi: 'in taxi',
};

function formatTime(iso: string | null, timezone: string): string {
  if (iso === null) return '--:--';
  return new Date(iso).toLocaleTimeString('it-IT', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(minor: number | null, currency: string): string | null {
  if (minor === null) return null;
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(minor / 100);
}

export interface TimelineProps {
  /** Alloggio della giornata: da dove si parte la mattina e dove si torna. */
  readonly base?: DayBase | null;
  readonly items: readonly ItineraryItemSummary[];
  readonly legs: readonly TransportLegSummary[];
  readonly timezone: string;
  readonly currency: string;
  readonly canEdit: boolean;
  readonly onMove?: (itemId: string, direction: -1 | 1) => void;
  readonly onToggleLock?: (item: ItineraryItemSummary) => void;
  readonly onRemove?: (itemId: string) => void;
  readonly onSelect?: (item: ItineraryItemSummary) => void;
}

export function Timeline({
  base = null,
  items,
  legs,
  timezone,
  currency,
  canEdit,
  onMove,
  onToggleLock,
  onRemove,
  onSelect,
}: TimelineProps): ReactNode {
  // La posizione serve come punto di partenza per il navigatore: se non c'è,
  // Google Maps usa comunque la posizione del telefono.
  const { position } = useGeolocation();

  if (items.length === 0) return null;

  return (
    <ol className="flex flex-col">
      {base !== null ? <BaseRow base={base} ruolo="partenza" posizione={position} /> : null}

      {items.map((item, index) => {
        const leg = legs.find((entry) => entry.toItemId === item.id) ?? null;
        const cost = formatMoney(item.estimatedCostMinor, item.estimatedCostCurrency ?? currency);
        const errors = item.warnings.filter((w) => w.severity === 'error');

        return (
          <li key={item.id}>
            {leg !== null && index > 0 ? (
              <LegRow
                leg={leg}
                da={items.find((entry) => entry.id === leg.fromItemId)}
                a={item}
                posizione={position}
              />
            ) : null}

            <div className="flex gap-3 py-1">
              <div className="w-[52px] shrink-0 pt-3 text-right">
                <span className="tabular text-[15px] font-semibold text-text">
                  {formatTime(item.plannedStart, timezone)}
                </span>
              </div>

              <div className="relative flex flex-col items-center">
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-4 h-2.5 w-2.5 shrink-0 rounded-full',
                    item.isLocked ? 'bg-brand-strong' : 'bg-border',
                  )}
                />
                {index < items.length - 1 ? (
                  <span aria-hidden="true" className="w-px flex-1 bg-border" />
                ) : null}
              </div>

              <div className="min-w-0 flex-1 pb-3">
                <button
                  type="button"
                  onClick={() => onSelect?.(item)}
                  className="w-full rounded-[var(--r-md)] px-1 py-2 text-left hover:bg-surface-sunken"
                >
                  <div className="flex items-start gap-2">
                    {/* La foto aiuta a riconoscere il posto quando ci si arriva. */}
                    {item.imageUrl !== null ? (
                      <PlaceThumb
                        url={item.imageUrl}
                        name={item.title}
                        attribution={item.imageAttribution}
                      />
                    ) : null}

                    <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                      <span className="text-[16px] font-medium leading-6 text-text">
                        {item.mealSlot !== null ? '🍽 ' : ''}
                        {item.title}
                      </span>
                      {item.isLocked ? (
                        <Lock
                          size={15}
                          className="mt-1 shrink-0 text-brand-strong"
                          aria-label="Tappa bloccata"
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] text-text-muted">
                    <span>{formatVisitDuration(item.durationMin)}</span>
                    {cost !== null ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{cost}</span>
                      </>
                    ) : (
                      <>
                        <span aria-hidden="true">·</span>
                        <DataBadge status="unavailable" label="costo non disponibile" />
                      </>
                    )}
                  </div>

                  {item.openingHours !== null && item.openingHours.status === 'unavailable' ? (
                    <div className="mt-1">
                      <DataBadge status="unavailable" label="Orari da verificare" />
                    </div>
                  ) : null}

                  {errors.map((warning) => (
                    <p
                      key={warning.code}
                      className="mt-1 rounded-[var(--r-sm)] bg-[var(--budget-blocked-soft)] px-2 py-1 text-[13px] text-[var(--budget-blocked)]"
                    >
                      {warning.message}
                    </p>
                  ))}
                </button>

                <div className="mt-1 flex items-center gap-1">
                  {item.lat !== null && item.lon !== null ? (
                    <button
                      type="button"
                      onClick={() => {
                        openNavigation(
                          { lat: item.lat as number, lon: item.lon as number, label: item.title },
                          { from: position, mode: 'walk' },
                        );
                      }}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-[var(--r-full)] border border-border px-3 text-[13.5px] font-medium text-brand-strong hover:bg-brand-soft"
                    >
                      <Navigation size={15} aria-hidden="true" />
                      Vai
                    </button>
                  ) : null}

                  {canEdit ? (
                    <>
                    <IconAction
                      label={`Sposta ${item.title} prima`}
                      disabled={index === 0}
                      onClick={() => onMove?.(item.id, -1)}
                      icon={<ArrowUp size={16} />}
                    />
                    <IconAction
                      label={`Sposta ${item.title} dopo`}
                      disabled={index === items.length - 1}
                      onClick={() => onMove?.(item.id, 1)}
                      icon={<ArrowDown size={16} />}
                    />
                    <IconAction
                      label={item.isLocked ? `Sblocca ${item.title}` : `Blocca ${item.title} a quest'ora`}
                      onClick={() => onToggleLock?.(item)}
                      icon={item.isLocked ? <Unlock size={16} /> : <Lock size={16} />}
                      active={item.isLocked}
                    />
                    <IconAction
                      label={`Rimuovi ${item.title}`}
                      onClick={() => onRemove?.(item.id)}
                      icon={<Trash2 size={16} />}
                    />
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </li>
        );
      })}

      {base !== null ? <BaseRow base={base} ruolo="rientro" posizione={position} /> : null}
    </ol>
  );
}

/**
 * L'alloggio come inizio e fine della giornata.
 *
 * Non e' una tappa: non dura, non costa, non si sposta. E' il punto da cui la
 * giornata parte e a cui torna — ed e' proprio quello che manca quando una
 * lista di tappe comincia direttamente dal primo museo, come se ci si
 * materializzasse davanti alla porta.
 *
 * Se l'indirizzo non e' stato riconosciuto lo si dice qui, dove serve: senza
 * coordinate l'alloggio non entra nel calcolo dei tragitti, e chi legge deve
 * poterlo capire subito invece di chiedersi perche' i tempi non tornano.
 */
function BaseRow({
  base,
  ruolo,
  posizione,
}: {
  base: DayBase;
  ruolo: 'partenza' | 'rientro';
  posizione: { lat: number; lon: number } | null;
}): ReactNode {
  const senzaPosizione = base.lat === null || base.lon === null;

  return (
    <li className="flex gap-3 py-1">
      <div className="w-[52px] shrink-0 pt-2 text-right text-[13px] text-text-subtle">
        {ruolo === 'partenza' ? 'da' : 'a'}
      </div>

      <div className="relative flex flex-col items-center">
        <span
          aria-hidden="true"
          className="mt-3 flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full border-2 border-brand-strong bg-surface"
        />
        <span aria-hidden="true" className="w-px flex-1 bg-border" />
      </div>

      <div className="min-w-0 flex-1 pb-2 pt-2">
        <p className="flex items-center gap-1.5 text-[15px] font-medium text-text">
          <House size={15} className="shrink-0 text-text-muted" aria-hidden="true" />
          <span className="truncate">{base.name}</span>
        </p>

        {base.address !== null ? (
          <p className="truncate text-[13px] text-text-muted">{base.address}</p>
        ) : null}

        {senzaPosizione ? (
          <p className="mt-1 rounded-[var(--r-sm)] bg-[var(--budget-warn-soft)] px-2 py-1 text-[13px] leading-5 text-[var(--budget-warn)]">
            Non ho trovato questo indirizzo sulla mappa: i tempi di spostamento
            non partono da qui. Correggilo negli alloggi.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => {
              openNavigation(
                { lat: base.lat as number, lon: base.lon as number, label: base.name },
                { from: posizione, mode: 'walk' },
              );
            }}
            className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 rounded-[var(--r-full)] border border-border px-3 text-[13.5px] font-medium text-brand-strong hover:bg-brand-soft"
          >
            <Navigation size={15} aria-hidden="true" />
            Vai
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Cosa sappiamo, e cosa non sappiamo, di uno spostamento.
 *
 * Il tempo e' una stima geometrica calcolata da noi, non un orario di
 * trasporto: dirlo e' l'unico modo onesto di presentarlo. Per le linee vere —
 * quale autobus, quale metropolitana, a che ora passa — servirebbero i dati
 * degli orari del trasporto pubblico, che questa installazione non ha. Invece
 * di inventarli, si passa la mano al navigatore, che li conosce.
 */
const SPIEGAZIONE_MEZZO: Record<TransportMode, string> = {
  walk: 'Tempo stimato camminando a passo normale, senza contare soste e semafori.',
  bike: 'Tempo stimato in bicicletta su percorso diretto. Non tiene conto di salite e piste ciclabili.',
  car: 'Tempo stimato in auto. Non tiene conto del traffico né della ricerca di un parcheggio.',
  transit:
    'Tempo stimato con i mezzi pubblici. Non conosco le linee né gli orari di questa città: aprendo il percorso trovi quale mezzo prendere e a che ora passa.',
  taxi: 'Tempo stimato in taxi. Non tiene conto del traffico.',
};

function LegRow({
  leg,
  da,
  a,
  posizione,
}: {
  leg: TransportLegSummary;
  da: ItineraryItemSummary | undefined;
  a: ItineraryItemSummary | undefined;
  posizione: { lat: number; lon: number } | null;
}): ReactNode {
  const Icon = MODE_ICONS[leg.mode];
  const [apertoInfo, setApertoInfo] = useState(false);

  // Il percorso si puo' aprire solo se sappiamo dove si arriva.
  const destinazione =
    a?.lat != null && a.lon != null ? { lat: a.lat, lon: a.lon, label: a.title } : null;
  const partenza =
    da?.lat != null && da.lon != null ? { lat: da.lat, lon: da.lon } : posizione;

  return (
    <div className="flex gap-3">
      <div className="w-[52px] shrink-0" />
      <div className="flex w-[10px] justify-center">
        <span aria-hidden="true" className="w-px flex-1 bg-border" />
      </div>
      <div className="min-w-0 flex-1 py-1.5">
        <div className="flex items-center gap-1.5 text-[13px] text-text-muted">
          <Icon size={15} aria-hidden="true" />
          <span>
            {formatDuration(leg.durationSec)} {MODE_LABELS[leg.mode]} ·{' '}
            {formatDistance(leg.distanceM)}
          </span>
          {leg.status === 'estimated' ? <DataBadge status="estimated" label="stimato" /> : null}

          <button
            type="button"
            aria-expanded={apertoInfo}
            aria-label={`Come spostarsi ${MODE_LABELS[leg.mode]}`}
            onClick={() => {
              setApertoInfo(!apertoInfo);
            }}
            className="ml-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--r-full)] text-text-subtle hover:bg-surface-sunken"
          >
            <Info size={15} aria-hidden="true" />
          </button>
        </div>

        {apertoInfo ? (
          <div className="mt-1 rounded-[var(--r-sm)] bg-surface-sunken px-3 py-2">
            <p className="text-[13px] leading-5 text-text-muted">
              {SPIEGAZIONE_MEZZO[leg.mode]}
            </p>
            {destinazione !== null ? (
              <button
                type="button"
                onClick={() => {
                  openNavigation(destinazione, { from: partenza, mode: leg.mode });
                }}
                className="mt-1.5 inline-flex min-h-[40px] items-center gap-1.5 text-[13.5px] font-medium text-brand-strong underline"
              >
                <Navigation size={14} aria-hidden="true" />
                Apri il percorso {MODE_LABELS[leg.mode]}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function IconAction({
  label,
  icon,
  onClick,
  disabled = false,
  active = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-[var(--r-sm)]',
        'text-text-muted transition-colors hover:bg-surface-sunken disabled:opacity-35',
        active ? 'text-brand-strong' : '',
      )}
    >
      {icon}
    </button>
  );
}
